import type { User, UserRole } from '@/features/users/types/user'
import { useAuthStore } from '@/features/auth/store/authStore'
import { UserRoleEnum } from '@/features/users/types/user'

export interface PermissionConfig {
  user: User | null
  activeRole: UserRole | null
}

export class PermissionHelper {
  private user: User | null
  private activeRole: UserRole | null

  constructor(config: PermissionConfig) {
    this.user = config.user
    this.activeRole = config.activeRole
  }

  // Core role checks
  isSystemAdmin(): boolean {
    return this.activeRole === UserRoleEnum.SYSTEM_ADMIN || this.activeRole === UserRoleEnum.DEVELOPER
  }

  isDeveloper(): boolean {
    return this.activeRole === UserRoleEnum.DEVELOPER
  }

  isTreasurer(): boolean {
    return this.activeRole === UserRoleEnum.TREASURER
  }

  isPastor(): boolean {
    return this.activeRole === UserRoleEnum.PASTOR
  }

  isAdminPastor(): boolean {
    return this.isPastor() && this.user?.isAdminPastor === true
  }

  isInventory(): boolean {
    return this.activeRole === UserRoleEnum.INVENTORY
  }

  isExpenses(): boolean {
    return this.activeRole === UserRoleEnum.EXPENSES
  }

  // Page access permissions
  canAccessUsers(): boolean {
    return this.isSystemAdmin()
  }

  canAccessReports(): boolean {
    return this.isSystemAdmin() || this.isTreasurer() || this.isPastor()
  }

  canAccessAnnualSummary(): boolean {
    return this.isSystemAdmin() || this.isTreasurer() || this.isPastor()
  }

  canAccessChurches(): boolean {
    return this.isSystemAdmin()
  }

  canAccessMembers(): boolean {
    return this.isSystemAdmin() || this.isTreasurer() || this.isPastor()
  }

  canAccessDonations(): boolean {
    return (
      this.isSystemAdmin() ||
      this.isTreasurer() ||
      this.isPastor() ||
      this.isAdminPastor()
    )
  }

  canAccessHome(): boolean {
    return (
      this.isSystemAdmin() ||
      this.isTreasurer() ||
      this.isPastor() ||
      this.isAdminPastor()
    )
  }

  // Feature permissions
  canCreateChurch(): boolean {
    return this.isSystemAdmin()
  }

  canEditChurch(churchId: string): boolean {
    if (this.isSystemAdmin()) return true
    if (this.isTreasurer()) {
      return this.user?.churchId?.includes(churchId) ?? false
    }
    return false // PASTOR cannot edit churches
  }

  canDeleteChurch(): boolean {
    return this.isSystemAdmin()
  }

  canCreateDonation(): boolean {
    return this.isSystemAdmin() || this.isTreasurer()
  }

  canEditDonation(donationChurchId: string): boolean {
    if (this.isSystemAdmin()) return true
    if (this.isTreasurer()) {
      return this.user?.churchId?.includes(donationChurchId) ?? false
    }
    return false // PASTOR cannot edit donations
  }

  canVerifyDonation(donationChurchId: string): boolean {
    if (this.isSystemAdmin()) return true
    if (this.isTreasurer()) {
      return this.user?.churchId?.includes(donationChurchId) ?? false
    }
    return false // PASTOR cannot verify donations
  }

  canDeleteDonation(): boolean {
    return this.isSystemAdmin()
  }

  canCreateMember(): boolean {
    return this.isSystemAdmin() || this.isTreasurer()
  }

  canEditMember(memberChurchId: string): boolean {
    if (this.isSystemAdmin()) return true
    if (this.isTreasurer()) {
      return this.user?.churchId?.includes(memberChurchId) ?? false
    }
    return false // PASTOR cannot edit members
  }

  canDeleteMember(): boolean {
    return this.isSystemAdmin()
  }

  canSendReceipt(): boolean {
    return this.isSystemAdmin() || this.isTreasurer()
  }

  // Get user's assigned churches (for filtering data)
  getUserAssignedChurches(): Array<string> {
    if (this.isSystemAdmin() || this.isAdminPastor()) return [] // Super admin sees all, so empty array means no filter
    return this.user?.churchId ?? []
  }

  // Check if user should see all data (no church filtering needed)
  shouldShowAllChurches(): boolean {
    return this.isSystemAdmin() || this.isAdminPastor()
  }

  // Check if user can access any data from a specific church
  canAccessChurchData(churchId: string): boolean {
    if (this.isSystemAdmin()) return true
    return this.user?.churchId?.includes(churchId) ?? false
  }

  // Settings permissions
  canAccessEmailSettings(): boolean {
    return this.isSystemAdmin()
  }

  canAccessDonationAutomation(): boolean {
    return this.isSystemAdmin()
  }

  canAccessUserProfile(): boolean {
    return (
      this.isSystemAdmin() ||
      this.isTreasurer() ||
      this.isPastor() ||
      this.isInventory() ||
      this.isExpenses()
    )
  }

  canAccessAuditLog(): boolean {
    return this.isSystemAdmin()
  }

  canAccessBackup(): boolean {
    return this.isSystemAdmin()
  }

  // Church-specific access (requires church ID comparison)
  canAccessChurch(churchId: string): boolean {
    if (this.isSystemAdmin()) return true
    return this.user?.churchId?.includes(churchId) ?? false
  }

  canAccessMember(memberChurchId: string): boolean {
    if (this.isSystemAdmin()) return true
    return this.user?.churchId?.includes(memberChurchId) ?? false
  }

  canAccessDonation(donationChurchId: string): boolean {
    if (this.isSystemAdmin()) return true
    return this.user?.churchId?.includes(donationChurchId) ?? false
  }

  canAccessInventory(): boolean {
    return (
      this.isSystemAdmin() ||
      this.isTreasurer() ||
      this.isPastor() ||
      this.isInventory() ||
      this.isAdminPastor()
    )
  }

  canDeleteInventoryCategory(): boolean {
    return this.isSystemAdmin()
  }

  canAccessExpenses(): boolean {
    return (
      this.isSystemAdmin() ||
      // this.isTreasurer() ||
      this.isPastor() ||
      this.isExpenses() ||
      this.isAdminPastor()
    )
  }

  canDeleteExpenseItem(): boolean {
    return this.isSystemAdmin()
  }

  canDeleteExpenseCategory(): boolean {
    return this.isSystemAdmin()
  }

  canViewPendingExpenses(): boolean {
    return this.isPastor() || this.isSystemAdmin() || this.isAdminPastor()
  }
}

// Hook for easy use in components
export function usePermissions(): PermissionHelper {
  const user = useAuthStore((state) => state.user)
  const activeRole = useAuthStore((state) => state.activeRole)

  return new PermissionHelper({ user, activeRole })
}
